// libraries.
import { Handle, Position, NodeProps } from "@xyflow/react";

// styles.
import {
  electricPurple50,
  electricPurple500,
  monserratSemiBold,
  robotoRegular,
  scarpaGray100,
  scarpaGray500,
  tomatoCoral500,
  turquoise600,
  white,
} from "@habitech/shared";

const categoryColors: Record<string, { bg: string; border: string; text: string }> = {
  transicion: {
    bg: electricPurple50,
    border: electricPurple500,
    text: electricPurple500,
  },
  bono: {
    bg: "#e6faf8",
    border: turquoise600,
    text: turquoise600,
  },
  descuento: {
    bg: "#fffbeb",
    border: "#f59e0b",
    text: "#b45309",
  },
  remate: {
    bg: "#fff3f3",
    border: tomatoCoral500,
    text: tomatoCoral500,
  },
};

const formatDayRange = (dayRange: [number, number | null]): string => {
  const [from, to] = dayRange;
  if (to === null) return `Día ${from}+`;
  return `Día ${from}–${to}`;
};

// ── Start Node ──
export const StartNode = ({ data }: NodeProps) => (
  <div
    data-testid="start-node"
    style={{
      background: electricPurple500,
      color: white,
      padding: "12px 24px",
      borderRadius: 12,
      fontFamily: monserratSemiBold,
      fontWeight: 600,
      fontSize: 13,
      textAlign: "center",
      minWidth: 200,
      boxShadow: "0 4px 12px rgba(124, 1, 255, 0.25)",
    }}
  >
    {data.label as string}
    <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
  </div>
);

// ── Decision Node (diamond) ──
export const DecisionNode = ({ data }: NodeProps) => (
  <div
    data-testid="decision-node"
    style={{
      width: 110,
      height: 80,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    }}
  >
    <div
      style={{
        width: "100%",
        height: "100%",
        background: scarpaGray100,
        border: `2px solid ${scarpaGray500}`,
        clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
        position: "absolute",
        top: 0,
        left: 0,
      }}
    />
    <span
      style={{
        fontFamily: monserratSemiBold,
        fontWeight: 600,
        fontSize: 10,
        textAlign: "center",
        lineHeight: 1.3,
        maxWidth: 60,
        position: "relative",
        zIndex: 1,
      }}
    >
      {data.label as string}
    </span>
    <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
    <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
  </div>
);

// ── Exceptions Group Node (single box with all exceptions as bullets) ──
export const ExceptionsGroupNode = ({ data }: NodeProps) => {
  const items = (data.exceptions as { name: string }[]) || [];

  return (
    <div
      data-testid="exceptions-group-node"
      style={{
        background: "#fff3f3",
        border: `2px solid ${tomatoCoral500}`,
        padding: "12px 16px",
        borderRadius: 10,
        minWidth: 200,
        maxWidth: 260,
        boxShadow: "0 2px 8px rgba(51, 51, 51, 0.08)",
      }}
    >
      <div
        style={{
          fontFamily: monserratSemiBold,
          fontWeight: 600,
          fontSize: 13,
          color: tomatoCoral500,
          marginBottom: 8,
        }}
      >
        {data.label as string}
      </div>
      {items.map((exc) => (
        <div
          key={exc.name}
          style={{
            fontFamily: robotoRegular,
            fontSize: 11,
            color: "#333",
            padding: "3px 0",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: tomatoCoral500,
              flexShrink: 0,
            }}
          />
          {exc.name}
        </div>
      ))}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

// ── End Node ──
export const EndNode = ({ data }: NodeProps) => (
  <div
    data-testid="end-node"
    style={{
      background: "#fff3f3",
      border: `2px dashed ${tomatoCoral500}`,
      padding: "10px 20px",
      borderRadius: 20,
      fontFamily: robotoRegular,
      fontWeight: 500,
      fontSize: 12,
      color: tomatoCoral500,
      textAlign: "center",
      minWidth: 180,
      boxShadow: "0 2px 8px rgba(51, 51, 51, 0.08)",
    }}
  >
    {data.label as string}
    <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
  </div>
);

// ── Route Node ──
export const RouteNode = ({ data }: NodeProps) => (
  <div
    data-testid="route-node"
    style={{
      background: electricPurple50,
      border: `2px solid ${electricPurple500}`,
      padding: "12px 20px",
      borderRadius: 12,
      fontFamily: monserratSemiBold,
      fontWeight: 600,
      fontSize: 13,
      color: electricPurple500,
      textAlign: "center",
      minWidth: 200,
      boxShadow: "0 2px 8px rgba(51, 51, 51, 0.08)",
    }}
  >
    {data.label as string}
    <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
    <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
  </div>
);

// ── Step Node (each incentive step) ──
const CATEGORY_LABELS: Record<string, string> = {
  transicion: "Transición",
  bono: "Bono",
  descuento: "Descuento",
  remate: "Remate",
};

export const StepNode = ({ data }: NodeProps) => {
  const category = (data.category as string) || "descuento";
  const colors = categoryColors[category] || categoryColors.descuento;
  const dayRange = data.dayRange as [number, number | null];
  const categoryLabel = CATEGORY_LABELS[category] || category;

  return (
    <div
      data-testid="step-node"
      style={{
        background: colors.bg,
        border: `2px solid ${colors.border}`,
        padding: "10px 16px",
        borderRadius: 10,
        minWidth: 180,
        maxWidth: 220,
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(51, 51, 51, 0.08)",
      }}
    >
      <div
        style={{
          fontFamily: robotoRegular,
          fontWeight: 500,
          fontSize: 9,
          color: colors.text,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: 2,
          opacity: 0.8,
        }}
      >
        {categoryLabel}
      </div>
      <div
        style={{
          fontFamily: monserratSemiBold,
          fontWeight: 600,
          fontSize: 12,
          color: colors.text,
          marginBottom: 4,
        }}
      >
        {data.label as string}
      </div>
      <div
        style={{
          fontFamily: robotoRegular,
          fontWeight: 600,
          fontSize: 12,
          color: colors.text,
        }}
      >
        {data.adjustment as string}
      </div>
      {dayRange && (
        <div
          style={{
            fontFamily: robotoRegular,
            fontSize: 10,
            color: scarpaGray500,
            marginTop: 2,
          }}
        >
          {formatDayRange(dayRange)}
        </div>
      )}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export const summaryNodeTypes = {
  startNode: StartNode,
  decisionNode: DecisionNode,
  exceptionsGroupNode: ExceptionsGroupNode,
  endNode: EndNode,
  routeNode: RouteNode,
  stepNode: StepNode,
};
